import { StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AccountRestrictionNotice } from './AccountRestrictionNotice';
import { PwaUpdateNotice } from './pwa/PwaUpdateNotice';
import { AchievementCelebrationWatcher } from './achievements/AchievementCelebrationWatcher';
import { AchievementsScreen } from './achievements/AchievementsScreen';
import { App } from './App';
import { CITY_CONTEXT_REFRESH_EVENT } from './city/cityContext';
import { EventsRouteShell } from './events/EventsRouteShell';
import { EventsScreen } from './events/EventsScreen';
import { fetchMineImpressions } from './guestImpressionsApi';
import { VerifiedAverageCheckBridge } from './map/VerifiedAverageCheckBridge';
import { ONBOARDING_COMPLETED_KEY, OnboardingScreen } from './onboarding/OnboardingScreen';
import { PostScreen } from './posts/PostScreen';
import { ProfileSettingsScreen } from './profile/ProfileSettingsScreen';
import type { ProfileSettingsData } from './profile/profileSettingsApi';
import { ScannerLauncher } from './ScannerLauncher';
import type { SessionResponse } from './ProfileScreen';
import { OfferDetailScreen } from './screens/OfferDetailScreen';
import { ImpressionFocusBridge } from './social/ImpressionFocusBridge';
import { UserProfileScreen } from './social/UserProfileScreen';
import {
  installShellHistory,
  isOwnerProfileHistoryState,
  isProfileSettingsPath,
  profileReferenceForPath,
  replaceOwnerProfilePath,
  syncShellHistoryFromLocation,
} from './shellHistory';
import { SearchUiBridge } from './ui/SearchUiBridge';
import './design-tokens.css';
import './styles.css';
import './search-ui.css';
import './layout-fixes.css';
import './impressions.css';
import './desktop-three-column.css';
import './feed.css';
import './pulse-reference.css';
import './pulse-reference-tail.css';
import './profile-reference.css';
import './profile-photos-corrective.css';
import './ui/foundation.css';
import './pulse-rebuild.css';
import './pulse-rebuild-final.css';
import './pulse-visual-qa.css';
import './map-adaptation.css';
import './tz2-map-visual-qa.css';
import './pulse-mobile-leaderboard.css';
import './tz6-owner-qa.css';
import './pulse-owner-qa-corrective.css';
import './userContext/user-context.css';
import './city/city-context-owner-qa.css';
import './ui/mobile-header-unification.css';

type CompletedScan = {
  status: string;
  receipt: null | {
    visit_id: string | null;
    venue: null | {
      name: string | null;
    };
  };
};

type ScanImpressionPrompt = {
  visitId: string;
  venueName: string;
};

type EventsRoute = {
  matched: boolean;
  eventId: string | null;
};

const AUTH_RETURN_TARGET_KEY = 'hopper.authReturnTarget';

function onboardingCompleted(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETED_KEY) === '1';
  } catch {
    return false;
  }
}

function shouldRenderOnboarding(): boolean {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/onboarding') return true;

  if (path === '/' && !onboardingCompleted()) {
    window.history.replaceState({}, '', '/onboarding');
    return true;
  }

  return false;
}

function isAchievementsPath(path = window.location.pathname): boolean {
  return /^\/profile\/achievements\/?$/.test(path);
}

function ProfileSettingsRoute() {
  const handleBack = () => window.location.assign('/profile');
  const handleSaved = (_profile: ProfileSettingsData) => {
    // The settings screen already renders the server-confirmed saved state.
  };

  return <ProfileSettingsScreen onBack={handleBack} onSaved={handleSaved} />;
}

function ProfileRouteLoading() {
  return (
    <main className="self-profile-page profile-family-route-loading" aria-label="Открываем профиль">
      <div className="social-loading">Определяем профиль…</div>
    </main>
  );
}

function OwnerProfileRuntime({ canonicalReference }: { canonicalReference: string }) {
  useEffect(() => {
    replaceOwnerProfilePath(canonicalReference);
    syncShellHistoryFromLocation();
  }, [canonicalReference]);

  return <StandardHopperRuntime />;
}

function PublicProfileRoute({ profileReference }: { profileReference: string }) {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [sessionResolved, setSessionResolved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    fetch('/api/v1/auth/session', {
      signal: controller.signal,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.ok ? response.json() as Promise<SessionResponse> : null)
      .then((value) => setSession(value))
      .catch(() => setSession(null))
      .finally(() => setSessionResolved(true));

    return () => controller.abort();
  }, []);

  if (!sessionResolved) {
    return <ProfileRouteLoading />;
  }

  const currentUser = session?.authenticated === true ? session.user : null;
  const currentSlug = currentUser?.slug?.trim() || null;
  const isOwner = Boolean(currentUser?.id && (
    profileReference === currentUser.id
    || (currentSlug !== null && profileReference === currentSlug)
  ));

  if (isOwner && currentUser?.id) {
    return <OwnerProfileRuntime canonicalReference={currentSlug ?? currentUser.id} />;
  }

  return (
    <EventsRouteShell externalSession={session} onSessionChange={setSession}>
      <UserProfileScreen
        profileReference={profileReference}
        authenticated={session?.authenticated === true}
        currentUserId={currentUser?.id ?? null}
        onBack={() => window.location.assign('/pulse')}
        onOpenFollowers={() => window.location.assign('/pulse')}
        onOpenFollowing={() => window.location.assign('/pulse')}
      />
    </EventsRouteShell>
  );
}

function legacyVenueMediaReferenceForPath(): string | null {
  const match = window.location.pathname.match(/^\/venue\/([^/]+)\/media\/?$/);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function replaceLegacyVenueMediaPath(venueReference: string): void {
  const url = new URL(window.location.href);
  url.pathname = `/venue/${encodeURIComponent(venueReference)}`;
  url.searchParams.set('tab', 'photos');

  window.history.replaceState(
    window.history.state ?? {},
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
}

function offerReferenceForPath(path = window.location.pathname): string | null {
  const match = path.match(/^\/offers\/([^/]+)\/?$/);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function postReferenceForPath(path = window.location.pathname): string | null {
  const match = path.match(/^\/posts\/([^/]+)\/?$/);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function eventsRouteForPath(path = window.location.pathname): EventsRoute {
  const match = path.match(/^\/events(?:\/([^/]+))?\/?$/);
  if (!match) return { matched: false, eventId: null };

  if (!match[1]) return { matched: true, eventId: null };

  try {
    return { matched: true, eventId: decodeURIComponent(match[1]) };
  } catch {
    return { matched: true, eventId: match[1] };
  }
}

function eventsAuthReturnRoute(): EventsRoute | null {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('auth') && !url.searchParams.has('auth_error')) return null;

  const target = sessionStorage.getItem(AUTH_RETURN_TARGET_KEY) ?? localStorage.getItem(AUTH_RETURN_TARGET_KEY);
  if (!target || !/^\/events(?:\/[^/]+)?\/?$/.test(target)) return null;

  sessionStorage.removeItem(AUTH_RETURN_TARGET_KEY);
  localStorage.removeItem(AUTH_RETURN_TARGET_KEY);
  window.history.replaceState({}, '', target);
  return eventsRouteForPath(target);
}

function StandardHopperRuntime() {
  const [dataRevision, setDataRevision] = useState(0);
  const [mapRevision, setMapRevision] = useState(0);
  const [scanImpressionPrompt, setScanImpressionPrompt] = useState<ScanImpressionPrompt | null>(null);
  const refreshMapData = useCallback(() => setMapRevision((value) => value + 1), []);

  useEffect(() => {
    const refreshResolvedCity = () => setMapRevision((value) => value + 1);
    window.addEventListener(CITY_CONTEXT_REFRESH_EVENT, refreshResolvedCity);
    return () => window.removeEventListener(CITY_CONTEXT_REFRESH_EVENT, refreshResolvedCity);
  }, []);

  const handleScanCompleted = useCallback((scan: CompletedScan) => {
    setDataRevision((value) => value + 1);
    setScanImpressionPrompt(null);

    const visitId = scan.receipt?.visit_id ?? null;

    if (scan.status !== 'confirmed' || !visitId) {
      return;
    }

    void fetchMineImpressions()
      .then((response) => {
        const alreadyExists = response.data.some((impression) => impression.visit_id === visitId);

        if (!alreadyExists) {
          setScanImpressionPrompt({
            visitId,
            venueName: scan.receipt?.venue?.name?.trim() || 'месте Хоппера',
          });
        }
      })
      .catch(() => {
        // Profile remains the fallback place to leave an impression.
      });
  }, []);

  const openScanImpression = useCallback(() => {
    if (!scanImpressionPrompt) {
      return;
    }

    const prompt = scanImpressionPrompt;
    setScanImpressionPrompt(null);

    const scannerClose = document.querySelector<HTMLButtonElement>('.scanner-close');
    scannerClose?.click();

    window.dispatchEvent(new CustomEvent('hopper:open-impression', {
      detail: {
        visitId: prompt.visitId,
        venueName: prompt.venueName,
      },
    }));
  }, [scanImpressionPrompt]);

  return (
    <>
      <App dataRevision={dataRevision} mapRevision={mapRevision} />
      <ImpressionFocusBridge />
      <VerifiedAverageCheckBridge onRefresh={refreshMapData} />
      <SearchUiBridge />
      <ScannerLauncher onCompleted={handleScanCompleted} />
      <AchievementCelebrationWatcher />

      {scanImpressionPrompt && (
        <div className="scan-impression-prompt" role="status">
          <div>
            <strong>Визит подтверждён</strong>
            <span>Расскажите, захотелось бы вам вернуться.</span>
          </div>
          <button type="button" onClick={openScanImpression}>Поделиться впечатлением</button>
          <button
            className="scan-impression-prompt__later"
            type="button"
            onClick={() => setScanImpressionPrompt(null)}
          >
            Позже
          </button>
        </div>
      )}
    </>
  );
}

function RootRuntime() {
  if (shouldRenderOnboarding()) {
    return <OnboardingScreen />;
  }

  if (isAchievementsPath()) {
    return (
      <EventsRouteShell>
        <AchievementsScreen />
      </EventsRouteShell>
    );
  }

  const authEventsRoute = eventsAuthReturnRoute();
  const directEventsRoute = authEventsRoute ?? eventsRouteForPath();
  if (directEventsRoute.matched) {
    return (
      <EventsRouteShell>
        <EventsScreen eventId={directEventsRoute.eventId} />
      </EventsRouteShell>
    );
  }

  if (isProfileSettingsPath()) {
    return (
      <EventsRouteShell>
        <ProfileSettingsRoute />
      </EventsRouteShell>
    );
  }

  const offerReference = offerReferenceForPath();
  if (offerReference) {
    return (
      <EventsRouteShell>
        <OfferDetailScreen offerId={offerReference} />
      </EventsRouteShell>
    );
  }

  const postReference = postReferenceForPath();
  if (postReference) {
    return (
      <EventsRouteShell>
        <PostScreen postId={postReference} />
      </EventsRouteShell>
    );
  }

  const legacyVenueMediaReference = legacyVenueMediaReferenceForPath();
  if (legacyVenueMediaReference) {
    replaceLegacyVenueMediaPath(legacyVenueMediaReference);
    return <StandardHopperRuntime />;
  }

  const profileReference = profileReferenceForPath();
  if (profileReference && !isOwnerProfileHistoryState()) {
    return <PublicProfileRoute profileReference={profileReference} />;
  }

  return <StandardHopperRuntime />;
}

const root = document.getElementById('root');

if (!root) {
  throw new Error('Root element was not found');
}

installShellHistory();

createRoot(root).render(
  <StrictMode>
    <RootRuntime />
    <AccountRestrictionNotice />
    <PwaUpdateNotice />
  </StrictMode>,
);
